import type {
  Simulation,
  CreateOriginSimulationPayload,
  CarrierAndMethodsSimulationPayload,
  HistoricalShipmentSelectData,
  UploadedShipmentData,
  RateUpdateStartPayload,
} from './types';
import { CARRIER_RATE_SELECT_STEP_KEY } from '../../components/simulation/guidedSimulations/CarrierRateSelectStep';
import { ADD_MARGIN_SURCHARGE_MODIFIER_STEP_KEY } from '../../components/simulation/guidedSimulations/AddMarginSurchargeModifier';
import { generateOriginRecordsFromUpload } from '../../components/simulation/utils';

const getAddCarrierMethodFromSimulationSetupData = (simulation: Simulation): CarrierAndMethodsSimulationPayload => {
  const { baseSimulationDetails, shipmentSelectionKey, carrierMethodSelectStep, carrierSettingsStepKey } =
    simulation.simulationSetupData ?? {};
  const { simulationId } = simulation;
  if (carrierMethodSelectStep && carrierSettingsStepKey && shipmentSelectionKey) {
    const postalCodes =
      generateOriginRecordsFromUpload((shipmentSelectionKey as UploadedShipmentData)?.selectedFiles ?? [])?.map(
        (record) => record.originPostalCode,
      ) ?? [];
    return {
      carrierId: carrierMethodSelectStep.carrier,
      origins: (shipmentSelectionKey as HistoricalShipmentSelectData)?.originIds,
      postalCodes,
      tenants: (shipmentSelectionKey as HistoricalShipmentSelectData)?.tenantIds,
      usingCarrierSelectionData: !!baseSimulationDetails?.includesHistoricalSelection,
      simulationId,
    };
  }
  throw new Error('Unexpected error');
};

const getOriginCreateFromSimulationSetupData = (simulation: Simulation): CreateOriginSimulationPayload => {
  const { originCreateStepKey, contractSelectStep, newOriginMappingStepKey } = simulation.simulationSetupData ?? {};
  const { simulationId } = simulation;
  if (originCreateStepKey && contractSelectStep && newOriginMappingStepKey) {
    return {
      simulationId,
      address: originCreateStepKey.address,
      schedule: originCreateStepKey.schedule,
      contractIds: contractSelectStep,
      destinationPostalCodes: newOriginMappingStepKey.value as any,
    };
  }
  throw new Error('Unexpected error');
};

const getFlatRateSimulationFromSetupData = (simulation: Simulation): RateUpdateStartPayload => {
  const { flatRateUpdateStep, shipmentSelectionKey } = simulation.simulationSetupData ?? {};
  const postalCodes =
    generateOriginRecordsFromUpload((shipmentSelectionKey as UploadedShipmentData)?.selectedFiles ?? [])?.map(
      (record) => record.originPostalCode,
    ) ?? [];
  if (flatRateUpdateStep) {
    return {
      postalCodes,
      ...flatRateUpdateStep,
    };
  }
  throw new Error('Unexpected error');
};
const getNewRatesheetSimulationFromSetupData = (simulation: Simulation): RateUpdateStartPayload => {
  const { newRatesheetStepKey, shipmentSelectionKey } = simulation.simulationSetupData ?? {};
  const postalCodes =
    generateOriginRecordsFromUpload((shipmentSelectionKey as UploadedShipmentData)?.selectedFiles ?? [])?.map(
      (record) => record.originPostalCode,
    ) ?? [];
  if (newRatesheetStepKey) {
    return {
      postalCodes,
      ...newRatesheetStepKey,
    };
  }
  throw new Error('Unexpected error');
};
const getSellRateSimulationPayloadFromSetupData = (simulation: Simulation) => {
  const rateSelectData = simulation?.simulationSetupData?.[CARRIER_RATE_SELECT_STEP_KEY];
  const { carrierRates } = rateSelectData ?? {};
  const sellRateCarrierSurchargeModifierData =
    simulation?.simulationSetupData?.[ADD_MARGIN_SURCHARGE_MODIFIER_STEP_KEY];
  const { applyCarrierSurcharges, amount, modifierType } = sellRateCarrierSurchargeModifierData ?? {};
  if (!carrierRates) {
    throw new Error('Unexpected error');
  }
  return {
    simulationId: simulation.simulationId,
    carrierRates,
    applyCarrierSurcharges,
    ...(applyCarrierSurcharges
      ? {
          amount: ['percentageDiscount', 'percentageIncrease'].includes(modifierType ?? '')
            ? Number(amount ?? 0) / 100
            : (amount ?? 0),
        }
      : {}),
    ...(applyCarrierSurcharges ? { modifierType } : {}),
  };
};
export interface AdvancedSimulationPayload {
  simulationId: string;
}
export const getPayloadForSimulationType = (simulation: Simulation) => {
  switch (simulation.simulationType) {
    case 'add_origin':
      return getOriginCreateFromSimulationSetupData(simulation);
    case 'add_carrier_method':
      return getAddCarrierMethodFromSimulationSetupData(simulation);
    case 'new_rate_sheet':
      return getNewRatesheetSimulationFromSetupData(simulation);
    case 'flat_rate_update':
      return getFlatRateSimulationFromSetupData(simulation);
    case 'sell_rate_simulation':
      return getSellRateSimulationPayloadFromSetupData(simulation);
    default:
      return { simulationId: simulation.simulationId };
  }
};
